import { Base64 } from 'js-base64';

export const ApiFilterMethods = {
  EQUAL: 'eq',
  CONTAINS: 'contains',
  STARTS_WITH: 'starts_with',
  ENDS_WITH: 'ends_with',
  NOT_EQUAL: 'neq',
  GREATER_THAN: 'gt',
  GREATER_THAN_EQUAL: 'gte',
  LESS_THAN: 'lt',
  LESS_THAN_EQUAL: 'lte',
  IN: 'in',
};

export class ApiFilter {
  /**
   *
   * @param {(array.<string>|string)} fields
   * @param {string} operation
   * @param {(string|number|boolean|array.<string>|array.<number>)} value
   */
  constructor(fields, operation, value) {
    this.f = typeof fields === 'string' ? [fields] : fields;
    this.o = operation;
    this.v = value;
  }
}

/**
 * @private fetchCallback
 */
export class ApiUrlBuilder {
  static SORT_ASC = 'asc';

  static SORT_DESC = 'desc';

  constructor(url = null, fetchCallback = null) {
    this.page = 0;
    this.perPage = null;
    this.paginated = null;
    this.sort = {};
    this.filters = [];
    this.embedFields = [];
    this.withFields = [];
    this.url = url;
    this.fetchCallback = fetchCallback;
    this.params = {};
  }

  /**
   * @return {object}
   */
  getUrlParameters() {
    const params = {};

    // Pagination
    if (this.page !== 0) {
      params.page = this.page;
    }

    if (this.perPage !== 0) {
      params.per_page = this.perPage;
    }

    if (this.paginated !== null) {
      params.paginate = this.paginated ? 1 : 0;
    }

    // Sorting
    if (Object.keys(this.sort).length) {
      const sortValues = [];
      Object.keys(this.sort).forEach(column => {
        const sign = this.sort[column] === ApiUrlBuilder.SORT_ASC ? '' : '-';
        sortValues.push(`${sign}${column}`);
      });

      params.sort = sortValues.join(',');
    }

    // Custom params
    if (Object.keys(this.params).length) {
      this.params.forEach(param => {
        params[param] = this.params[param];
      });
    }

    // Filtering
    if (this.filters.length) {
      params.filters = Base64.encode(JSON.stringify(this.filters));
    }

    // With embeds
    if (this.embedFields.length) {
      params.embed = this.embedFields.join(',');
    }

    // With enumerators
    if (this.withFields.length) {
      params.with = this.withFields.join(',');
    }

    return params;
  }

  /**
   * Set page.
   * @param {number} page
   * @return {ApiUrlBuilder}
   */
  setPage(page) {
    this.page = page ? Number(page) : 1;
    return this;
  }

  /**
   * Set maximal number of items per each page.
   * @param {number} value
   * @return {ApiUrlBuilder}
   */
  setPerPage(value) {
    this.perPage = value ? Number(value) : 10;
    return this;
  }

  /**
   * Turn pagination on or off.
   * @param {boolean} off
   * @return {ApiUrlBuilder}
   */
  turnOffPagination(off = true) {
    this.paginated = !off;
    return this;
  }

  /**
   * @param name
   * @param {(string|number|boolean)} value
   * @returns {ApiUrlBuilder}
   */
  setParam(name, value) {
    this.params[name] = value;
    return this;
  }

  /**
   * Select only data where column value equals to given value.
   * @param {string} column
   * @param {(string|number|boolean)} value
   * @returns {ApiUrlBuilder}
   */
  whereEquals(column, value) {
    const filter = new ApiFilter(column, ApiFilterMethods.EQUAL, value);
    this.filters.push(filter);
    return this;
  }

  /**
   * Select only data where column value is not equal to given value.
   * @param {string} column
   * @param {(string|number|boolean)} value
   * @returns {ApiUrlBuilder}
   */
  whereNot(column, value) {
    const filter = new ApiFilter(column, ApiFilterMethods.NOT_EQUAL, value);
    this.filters.push(filter);
    return this;
  }

  /**
   * Select only data where value of specified columns starts with given value.
   * @param {(string|array.<string>)} columns
   * @param {string} value
   * @returns {ApiUrlBuilder}
   */
  whereStartsWith(columns, value) {
    const filter = new ApiFilter(columns, ApiFilterMethods.STARTS_WITH, value);
    this.filters.push(filter);
    return this;
  }

  /**
   * Select only data where value of specified columns ends with given value.
   * @param {(string|array.<string>)} columns
   * @param {string} value
   * @returns {ApiUrlBuilder}
   */
  whereEndsWith(columns, value) {
    const filter = new ApiFilter(columns, ApiFilterMethods.ENDS_WITH, value);
    this.filters.push(filter);
    return this;
  }

  /**
   * Select only data where value of specified columns contains given value.
   * @param {(string|array.<string>)} columns
   * @param {string} value
   * @returns {ApiUrlBuilder}
   */
  whereContains(columns, value) {
    const filter = new ApiFilter(columns, ApiFilterMethods.CONTAINS, value);
    this.filters.push(filter);
    return this;
  }

  /**
   * Select only data where value of specified column is withing given values.
   * @param {string[]} columns
   * @param {(array.<string>|array.<number>)} value
   * @returns {ApiUrlBuilder}
   */
  whereIn(columns, value) {
    const filter = new ApiFilter(columns, ApiFilterMethods.IN, value);
    this.filters.push(filter);
    return this;
  }

  /**
   * Select only data where value of specified column is greater than given value.
   * @param {(string|array.<string>)} columns
   * @param {number} value
   * @returns {ApiUrlBuilder}
   */
  whereGreaterThan(columns, value) {
    const filter = new ApiFilter(columns, ApiFilterMethods.GREATER_THAN, value);
    this.filters.push(filter);
    return this;
  }

  /**
   * Select only data where value of specified column is greater or equal to given value.
   * @param {(string|array.<string>)} columns
   * @param {number} value
   * @returns {ApiUrlBuilder}
   */
  whereGreaterOrEqual(columns, value) {
    const filter = new ApiFilter(columns, ApiFilterMethods.GREATER_THAN_EQUAL, value);
    this.filters.push(filter);
    return this;
  }

  /**
   * Select only data where value of specified column is less than given value.
   * @param {(string|array.<string>)} columns
   * @param {number} value
   * @returns {ApiUrlBuilder}
   */
  whereLessThan(columns, value) {
    const filter = new ApiFilter(columns, ApiFilterMethods.LESS_THAN, value);
    this.filters.push(filter);
    return this;
  }

  /**
   * Select only data where value of specified column is less or equal to given value.
   * @param {(string|array.<string>)} columns
   * @param {number} value
   * @returns {ApiUrlBuilder}
   */
  whereLessOrEqual(columns, value) {
    const filter = new ApiFilter(columns, ApiFilterMethods.LESS_THAN_EQUAL, value);
    this.filters.push(filter);
    return this;
  }

  /**
   * Fetch data with specified fields.
   * @param {(string|array.<string>)} fields
   * @return {ApiUrlBuilder}
   */
  embed(fields) {
    this.embedFields = [];
    let relationsToFetch = fields;

    if (typeof fields === 'string') {
      relationsToFetch = fields.split(',');
    }

    if (Array.isArray(relationsToFetch)) {
      relationsToFetch.forEach(relation => {
        if (typeof relation === 'string') {
          this.embedFields.push(relation);
        } else {
          console.error('Embed field name must be string!', relation);
        }
      });
    } else {
      console.error('Parameter must be string or array!', fields);
    }

    return this;
  }

  /**
   * Fetch specified enumerators.
   * @param {(string|array.<string>)} fields
   * @return {ApiUrlBuilder}
   */
  with(fields) {
    this.withFields = [];
    let relationsToFetch = fields;

    if (typeof fields === 'string') {
      relationsToFetch = fields.split(',');
    }

    if (Array.isArray(relationsToFetch)) {
      relationsToFetch.forEach(relation => {
        if (typeof relation === 'string') {
          this.withFields.push(relation);
        } else {
          console.error('Enumerator name must be string!', relation);
        }
      });
    } else {
      console.error('Parameter must be string or array!', fields);
    }

    return this;
  }

  /**
   * Sort results by given columns in given direction.
   * @param {(string|array.<string>|object)} columns
   * @param {string} direction='asc' use 'asc' or 'desc'
   * @return {ApiUrlBuilder}
   */
  sortBy(columns, direction = ApiUrlBuilder.SORT_ASC) {
    let columnsToParse = columns;
    const sortBy = {};

    // When string given (e.g. '-first_name,email'), split column to array
    if (typeof columns === 'string') {
      columnsToParse = columns.split(',');
    }

    // Process array (e.g. ['-first_name', 'email'])
    if (Array.isArray(columnsToParse)) {
      columnsToParse.forEach(column => {
        if (typeof column !== 'string') {
          console.error('Column name for sorting must be string!', columns);
          return;
        }

        if (column.charAt(0) === '-') {
          // e.g. '-first_name' -> sort column 'first_name' in descendant order
          const columnName = column.substr(1);
          sortBy[columnName] = ApiUrlBuilder.SORT_DESC;
        } else {
          // e.g. 'email' -> sort column 'email' in given order
          sortBy[column] = direction;
        }
      });
    } else if (typeof columns === 'object') {
      // eg. {'first_name': 'desc', 'email': 'asc'}
      columnsToParse.forEach(column => {
        if (Object.prototype.hasOwnProperty.call(columnsToParse, column)) {
          return;
        }

        sortBy[column] =
          columnsToParse[column].toLowerCase() === ApiUrlBuilder.SORT_DESC
            ? ApiUrlBuilder.SORT_DESC
            : ApiUrlBuilder.SORT_ASC;
      });
    } else {
      console.error('Sorting definition must be string, array or object!', columns);
    }

    this.sort = sortBy;
    return this;
  }

  /**
   * Sort results by given columns in descendant order.
   * @param {(string|array.<string>|object)} columns
   * @return {ApiUrlBuilder}
   */
  sortByDesc(columns) {
    this.sortBy(columns, ApiUrlBuilder.SORT_DESC);
    return this;
  }

  /**
   * Set filters.
   * @param {array.<ApiFilter>} filters
   * @return {ApiUrlBuilder}
   */
  setFilters(filters) {
    if (!Array.isArray(filters)) {
      return this;
    }

    const validFilters = [];

    filters.forEach(filter => {
      if (filter instanceof ApiFilter) {
        validFilters.push(filter);
      } else if (
        typeof filter === 'object' &&
        Object.prototype.hasOwnProperty.call(filter, 'f') &&
        Object.prototype.hasOwnProperty.call(filter, 'o') &&
        Object.prototype.hasOwnProperty.call(filter, 'v')
      ) {
        const newFilter = new ApiFilter(filter.f, filter.o, filter.v);
        validFilters.push(newFilter);
      } else {
        console.error('Filter definition must be valid object!', filter);
      }
    });

    this.filters = validFilters;
    return this;
  }
}
